import { Slot } from "@radix-ui/react-slot";
import { LinkComponent as RouterLinkComponent, createLink } from "@tanstack/react-router";
import { AnchorHTMLAttributes, ComponentProps, forwardRef } from "react";

import { cn } from "@/lib/utils";

type InternalLinkProps = {
    asChild?: boolean;
    external?: false;
};

type ExternalLinkProps = {
    asChild?: boolean;
    external: true;
};

type LinkProps = (InternalLinkProps | ExternalLinkProps) & AnchorHTMLAttributes<HTMLAnchorElement>;

const LinkComponent = forwardRef<HTMLAnchorElement, LinkProps>(
    ({ className, asChild = false, external, ...props }, ref) => {
        if (external) {
            const Comp = asChild ? Slot : "a";

            return <Comp ref={ref} className={cn(className)} target="_blank" rel="noopener noreferrer" {...props} />;
        }

        if (asChild) {
            return <Slot ref={ref} className={cn(className)} {...props} />;
        }

        return <a ref={ref} className={cn(className)} {...props} />;
    }
);

export const CreatedLinkComponent = createLink(LinkComponent);

type CreatedLinkProps = ComponentProps<typeof CreatedLinkComponent>;

const LinkWithRef = forwardRef<HTMLAnchorElement, CreatedLinkProps>(function Link(props, ref) {
    return <CreatedLinkComponent ref={ref} preload="intent" {...props} />;
});

LinkWithRef.displayName = "Link";

export const Link = LinkWithRef as RouterLinkComponent<typeof LinkComponent>;
